import { ReactNode, useCallback, useEffect, useState } from "react";
import FirstTimeLoader from "./FirstTimeLoader";
import { getRouteArtistSlug, isReservedRootPath } from "@/utils/routePaths";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { prefetchListRouteData } from "@/utils/routePrefetch";

const FIRST_VISIT_KEY = "truevined_first_visit";
const releasePageTypes = new Set(["songs", "albums"]);
const nonReleasePageTypes = new Set(["bio", "songs", "albums", "videos"]);

interface LoadingStateManagerProps {
  children: ReactNode;
}

const isCurrentRouteReleasePage = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  return (
    ["admin", "newsletter", "members", "branches", "merch", "games"].includes(parts[0]) ||
    (isReservedRootPath(parts[0]) && ["redirect", "r"].includes(parts[0])) ||
    (parts.length === 2 && releasePageTypes.has(parts[0])) ||
    (parts.length === 3 && releasePageTypes.has(parts[1])) ||
    (parts.length === 2 && !nonReleasePageTypes.has(parts[1]))
  );
};

const formatRouteArtistName = (value?: string) => {
  if (!value) return "Truevined";
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getCurrentRouteArtistName = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const routeArtist = getRouteArtistSlug(parts);
  return formatRouteArtistName(routeArtist);
};

function LoadingStateManager({ children }: LoadingStateManagerProps) {
  const [isFirstTimeLoading, setIsFirstTimeLoading] = useState(() => {
    if (!safeGetItem(FIRST_VISIT_KEY)) {
      return !isCurrentRouteReleasePage();
    }
    return false;
  });

  useEffect(() => {
    if (!safeGetItem(FIRST_VISIT_KEY)) {
      safeSetItem(FIRST_VISIT_KEY, "true");
    }
  }, []);

  useEffect(() => {
    if (isFirstTimeLoading) prefetchListRouteData(window.location.pathname);
  }, [isFirstTimeLoading]);

  const handleFirstTimeLoadComplete = useCallback(() => {
    setIsFirstTimeLoading(false);
  }, []);

  if (isFirstTimeLoading) {
    return (
      <FirstTimeLoader
        onComplete={handleFirstTimeLoadComplete}
        artistName={getCurrentRouteArtistName()}
      />
    );
  }

  return <>{children}</>;
}

export default LoadingStateManager;
